import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { MFAMethod, VerifyAuthResponse } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import {
  authnClient,
  navigateToUrl,
  knownMobileAppClientIDs,
  hydraSessionStore,
  AuthFetchHTTPError,
} from '@frontend/auth';
import {
  AuthStorage,
  LOGIN_ERROR_CAUSE,
  SsoTenant,
  isWeaveTokenActive,
  localStorageHelper,
} from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  EmailField,
  FormRow,
  Heading,
  PasswordField,
  Text,
  TextLink,
  useForm,
  useFormField,
  PasswordProps,
  CheckboxField,
  BaseFieldProps,
  useControlledField,
  Button,
} from '@frontend/design-system';
import { SignInError, ContentLoader, SignInErrorTypeKey } from '../components';
import { hydraShellService, signInWithPassword } from '../services';
import { getLoginFeatures } from '../services/features-service';
import { getRedirectURL } from '../services/fetch-wrapper';
import { FormWrap } from './form-wrap';
import { MFAForm } from './mfa-form';

interface Props {
  username?: string;
  error?: SignInErrorTypeKey;
  routerNavigate: UseNavigateResult<`/`>;
}

export const LoginFormComponent = ({ username, error, routerNavigate }: Props) => {
  const { t } = useTranslation('SignIn');

  const currentState = useMemo(() => hydraShellService.getCurrentState(), []);
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState(currentState === 'consent');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<MFAMethod[]>([]);
  const [challenge, setChallenge] = useState('');
  const [showDisplayNames, setShowDisplayNames] = useState(false);
  const [requestWebauthnRegistration, setRequestWebauthnRegistration] = useState(false);

  const [errorCode, setErrorCode] = useState<SignInErrorTypeKey | undefined>(error);
  const [signInError, setSignInError] = useState<AuthFetchHTTPError | undefined>();
  const [isError, setIsError] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [showWebAuthLogin, setShowWebAuthLogin] = useState<boolean>(false);
  const [isInMobileApp, setIsInMobileApp] = useState(false);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [redirectURL, setRedirectURL] = useState(appConfig.DEFAULT_REDIRECT);

  const [rememberMe, setRememberMe] = useState(false);
  const [loginFeatures, setLoginFeatures] = useState<string[]>([]);

  const rememberedUsername = localStorageHelper.get(AuthStorage.remembered_username);
  const showAuthTenantSSO = appConfig.SHOW_AUTH_TENANT_SSO === 'true';

  const usernameProps = useFormField({
    type: 'email',
    required: true,
    value: username || rememberedUsername,
  });

  const handleForgotPassword = (username?: string) => {
    return routerNavigate({
      to: '/reset-password',
      search: {
        username: username ?? '',
      },
    });
  };

  const signInWithPasswordHandler = async (username: string, password: string) => {
    setIsCheckingPassword(true);
    setIsError(false);

    const timeOnPage = Math.round(Date.now() - window.performance.timeOrigin);

    //if no login challenge is in url, then redirect user back to portal link
    const loginChallenge = hydraShellService.getLoginChallenge();
    if (loginChallenge === '') {
      sentry.error({
        error: 'No login challenge found, forcing refresh before login',
        topic: 'auth',
        team: 'users-infra',
        sampleRate: 1,
        addContext: {
          name: 'time on page',
          context: {
            timeOnPage,
          },
        },
      });
      // get a new login challenge
      setTimeout(() => {
        navigateToUrl(appConfig.DEFAULT_REDIRECT, { reason: 'no login challenge found' });
      }, 500);
      return new Promise<never>(() => {});
    }

    // send username and password to backend
    // if successful, redirect to the requesting application
    // if unsuccessful, set error message
    try {
      const res = await signInWithPassword(username, password, requestWebauthnRegistration);
      if (res && typeof res === 'object' && 'token' in res) {
        if (res.pwned) {
          return routerNavigate({
            to: '/reset-password',
            search: {
              username: username ?? '',
              errorCode: 'pawnedPassword',
            },
          });
        }
        if (res.mfa && res.mfa?.required === true) {
          setChallenge(res?.challenge ?? '');
          setMfaEnabled(true);
          setMfaMethods(res.mfa.method ?? []);
          if (!res.mfa.codeSent) {
            setShowDisplayNames(true);
          }
          return;
        }
        if (res.token) {
          return handleAcceptOAuth2Flow({
            // a null response on login means we are not in MFA flow and don't have it setup, so we should ask them to setup MFA
            shouldRedirectToMFAPromptPage: res.mfa?.showSetup === true,
          });
        } else {
          throw new Error('No token returned');
        }
      }
    } catch (err) {
      handleFailedLogin(err as Error);
    }
  };

  const handleFailedLogin = (err?: Error | AuthFetchHTTPError) => {
    setIsCheckingPassword(false);
    setIsLoggingIn(false);
    setIsError(true);

    if (err?.cause === LOGIN_ERROR_CAUSE.ipRestricted) {
      setErrorCode('ipRestricted');
      setSignInError(err as AuthFetchHTTPError);
    } else {
      setErrorCode(loginAttempts > 2 ? 402 : 401); // NOTE the errorCode is interpreted by sign-in-error component
      setLoginAttempts((val) => val + 1);
      if (loginAttempts > 2) {
        setErrorCode(402);
      }
    }
  };

  const handleWebAuthnSignInResponse = async (res: VerifyAuthResponse) => {
    if (res.token) {
      return handleAcceptOAuth2Flow();
    } else {
      handleFailedLogin();
    }
  };

  const handleWebAuthnError = (_: Error) => {
    setIsError(true);
    setErrorCode('webauthnError');
    // TODO: update sign-in-error component to support a debug message for helping users and support
  };

  const handleAcceptOAuth2Flow = async (mfaOpts?: { shouldRedirectToMFAPromptPage: boolean }) => {
    setIsCheckingPassword(false);
    setIsLoggingIn(true);
    const currentState = hydraShellService.getCurrentState();

    switch (currentState) {
      case 'login':
        try {
          const res = await hydraShellService.acceptOAuth2Flow();
          if (!!res.redirectTo) {
            if (rememberMe) {
              localStorageHelper.create(AuthStorage.remembered_username, usernameProps.value);
            } else {
              localStorageHelper.delete(AuthStorage.remembered_username);
            }
            if (mfaOpts?.shouldRedirectToMFAPromptPage) {
              sentry.addBreadcrumb({
                category: 'auth',
                message: 'Routing to MFA post-login prompt page',
                type: 'navigation',
              });
              routerNavigate({
                to: '/mfa-prompt/',
                search: {
                  redirectTo: res.redirectTo,
                },
              });
              return;
            }
            return navigateToUrl(res.redirectTo, { reason: 'accept oauth2 flow redirect' });
          }
          throw new Error('missing redirectTo in AcceptLoginResponse');
        } catch (e: any) {
          if (e.message === 'login request was cancelled') {
            console.warn('user logging in does not match the previous user, we need to get a new login challenge');
            return navigateToUrl(redirectURL || appConfig.DEFAULT_REDIRECT, {
              reason: 'error in accept oauth2 flow - login request was cancelled',
              error: e,
            });
          } else {
            sentry.error({
              topic: 'handleAcceptOAuth2Flow',
              team: 'users-infra',
              error: `Error in accept oauth2 flow - ${e}`,
            });
            throw e;
          }
        }
      default:
        // If we are in this state, then we don't know what client initialized the flow so send them to the default redirect
        setIsLoggingIn(true);
        return navigateToUrl(appConfig.DEFAULT_REDIRECT, { reason: 'unknown client, default redirect' });
    }
  };

  useEffect(() => {
    setIsCheckingStatus(true);
    const isAuthed = isWeaveTokenActive();
    if (currentState === 'login') {
      handleInitializeOAuth2Flow(isAuthed);
      return;
    }
    if (currentState === 'consent') {
      handleConsentQueryParam();
      return;
    }
    if (currentState === 'logout') {
      handleLogoutFlow();
      return;
    }
    if (currentState === 'none') {
      setIsCheckingStatus(false);
    }
  }, [currentState]);

  useEffect(() => {
    const isAuthed = isWeaveTokenActive();
    setShowWebAuthLogin(loginFeatures.includes('webauthn') && !isAuthed);
  }, [loginFeatures]);

  useEffect(() => {
    if (rememberedUsername) setRememberMe(true);

    // initialize login features
    getLoginFeatures()
      .then((features) => {
        setLoginFeatures(features);
      })
      .catch((_) => {
        setLoginFeatures([]);
      });
  }, []);

  const handleInitializeOAuth2Flow = async (isAuthed: boolean) => {
    let localRedirectURL: string | undefined;

    try {
      const res = await hydraShellService.initializeOAuth2Flow();
      setIsCheckingStatus(false);
      setIsInMobileApp(knownMobileAppClientIDs.has(res.client?.clientId || ''));

      // Store redirectURL for later use in the catch block
      if (res.requestUrl) {
        localRedirectURL = getRedirectURL(res.requestUrl);
        setRedirectURL(localRedirectURL || appConfig.DEFAULT_REDIRECT);
      }

      // if we are already authenticated, we can accept the OAuth2 flow
      if (isAuthed) {
        setIsLoggingIn(true);
        const res = await hydraShellService.acceptOAuth2Flow();
        return navigateToUrl(res.redirectTo || localRedirectURL || appConfig.DEFAULT_REDIRECT);
      }
      return;
    } catch (e: any) {
      if (e.message === 'login request was cancelled') {
        // This is an expected error that occures when a user leaves their browser open on the login page for extended periods
        // All we need to do is get a new login challenge from Hydra by restarting the login flow
        console.warn('user logging in does not match the previous user, we need to get a new login challenge');
        return navigateToUrl(redirectURL || appConfig.DEFAULT_REDIRECT, { reason: 'error in oauth2 flow', error: e });
      }
      setIsCheckingStatus(false);
      // alerts.error(t('Error initializing OAuth2 flow'));
      return navigateToUrl(redirectURL || appConfig.DEFAULT_REDIRECT, { reason: 'error in oauth2 flow', error: e });
    }
  };

  const handleConsentQueryParam = async () => {
    let redirectURL: string | undefined;
    setIsCheckingConsent(true);
    try {
      const res = await hydraShellService.initializeConsentFlow();
      setIsCheckingStatus(false);
      // intentionally not turning off consent state here
      // Store redirectURL for later use in the catch block
      if (res.requestUrl) {
        redirectURL = getRedirectURL(res.requestUrl);
      }
      return await hydraShellService.acceptConsentFlow(res.scope || [], res.client?.clientId);
    } catch (error) {
      setIsCheckingStatus(false);
      // Use redirectURL in the catch block
      return navigateToUrl(redirectURL || appConfig.DEFAULT_REDIRECT, {
        reason: 'error in handleConsentQueryParam',
        error,
      });
    }
  };

  const handleLogoutFlow = async () => {
    return hydraShellService
      .initializeLogoutFlow()
      .then(async () => {
        const signInMethod = await authnClient.getSignInMethod();
        if (signInMethod === 'okta') {
          return authnClient.signOut(); // redirects away from this page and returns to the /sign-out/callback route, there we will call acceptLogoutFlow
        }
        return hydraShellService.acceptLogoutFlow();
      })
      .then(async (res) => {
        return authnClient.signOut({ logoutRedirect: res?.redirectTo }); // redirects
      })
      .catch((err) => {
        console.error('Error during handleLogoutFlow', err);
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: err,
          addContext: {
            name: 'auth',
            context: {
              errMessage: `Error during hydra-shell logout flow`,
            },
          },
        });
        setIsCheckingStatus(false);
        const lsKeysToRemove = [
          AuthStorage.jti,
          AuthStorage.weave_token,
          AuthStorage.token_received_at,
          AuthStorage.sign_in_method,
          AuthStorage.consent_challenge,
          AuthStorage.logout_challenge,
        ];
        lsKeysToRemove.forEach((key) => localStorageHelper.delete(key));
        hydraSessionStore.clear();

        // attempt to recover from this state by sending user to default redirect (nwx), which will redirect to sign-in again
        navigateToUrl(appConfig.DEFAULT_REDIRECT, {
          reason: 'error in logout flow, redirecting to default redirect',
          error: err,
        });
      })
      .finally(() => setIsCheckingStatus(false));
  };

  if (isLoggingIn || isCheckingConsent) {
    return (
      <>
        <FormWrap>
          <ContentLoader message={t('Logging in...')} show />
        </FormWrap>
      </>
    );
  }

  const oktaSignIn = async (): Promise<void> => {
    authnClient.changeAuthMethod('okta');
    await authnClient.signIn();
  };

  return (
    <>
      {/* Wrapping in a ternary just so that we don't have to worry about unused refs */}
      {mfaEnabled ? (
        <FormWrap>
          <MFAForm
            mfaMethods={mfaMethods}
            onHandleMFAViewToggle={setMfaEnabled}
            handleSignIn={handleAcceptOAuth2Flow}
            challenge={challenge}
            showDisplayNames={showDisplayNames}
            onToggleDisplayNames={setShowDisplayNames}
            registerWebauthn={requestWebauthnRegistration}
            rememberMe={rememberMe}
            onIsCheckingPasswordToggle={setIsCheckingPassword}
            username={usernameProps.value}
          />
        </FormWrap>
      ) : (
        <>
          <FormWrap>
            <Heading>{t('Welcome back')}</Heading>
            <Text>{t('Log in to your Weave account')}</Text>
            <LoginFormInner
              isCheckingPassword={isCheckingPassword}
              isCheckingStatus={isCheckingStatus}
              onSubmitPassword={signInWithPasswordHandler}
              onSubmitWorkforceSSO={oktaSignIn}
              onClickForgotPassword={(username) => handleForgotPassword(username)}
              shouldFocus={() => !showWebAuthLogin}
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
              usernameProps={usernameProps}
              showWebAuthLogin={showWebAuthLogin}
              showAuthTenantSSO={showAuthTenantSSO}
              isInMobileApp={isInMobileApp}
              requestWebauthnRegistration={requestWebauthnRegistration}
              setRequestWebauthnRegistration={setRequestWebauthnRegistration}
              onHandleWebauthnSignIn={handleWebAuthnSignInResponse}
              onHandleWebauthnError={handleWebAuthnError}
              routerNavigate={routerNavigate}
            />
          </FormWrap>
        </>
      )}

      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 15%;
          min-width: 300px;
          max-width: 400px;
        `}
      >
        {isError ? <SignInError errorCode={errorCode} signInError={signInError} /> : null}
      </div>
    </>
  );
};

type FormProps = {
  isCheckingPassword: boolean;
  isCheckingStatus: boolean;
  shouldFocus: () => boolean;
  onClickForgotPassword: (username?: string) => void;
  onSubmitWorkforceSSO: () => Promise<void>;
  onSubmitPassword: (username: string, password: string) => Promise<void>;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
  usernameProps: BaseFieldProps<string>;
  showWebAuthLogin: boolean;
  showAuthTenantSSO: boolean;
  isInMobileApp: boolean;
  requestWebauthnRegistration: boolean;
  setRequestWebauthnRegistration: (value: boolean) => void;
  onHandleWebauthnSignIn: (res: VerifyAuthResponse) => void;
  onHandleWebauthnError: (err: Error) => void;
  routerNavigate: UseNavigateResult<`/`>;
};
const LoginFormInner = ({
  isCheckingPassword,
  isCheckingStatus,
  onSubmitPassword,
  onSubmitWorkforceSSO,
  onClickForgotPassword,
  shouldFocus,
  rememberMe,
  setRememberMe,
  usernameProps,
  showWebAuthLogin,
  showAuthTenantSSO,
  isInMobileApp,
  requestWebauthnRegistration,
  setRequestWebauthnRegistration,
  onHandleWebauthnSignIn,
  onHandleWebauthnError,
  routerNavigate,
}: FormProps) => {
  const { t } = useTranslation('SignIn');
  const passwordProps = useFormField({ type: 'password', required: true, minChars: 1 });
  const usernameRef = useRef<HTMLDivElement>(null);
  const isWorkforceSSO = usernameProps.value.endsWith('@getweave.com');
  const [autofocusAlreadyRun, setAutofocusAlreadyRun] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  //autofocus on the username field
  useEffect(() => {
    if (usernameRef?.current && shouldFocus()) {
      //setting a timeout here to allow the browser enough time to attempt an autocomplete before we focus
      const timeout = setTimeout(() => {
        if (shouldFocus() && !autofocusAlreadyRun) {
          usernameRef?.current?.querySelector('input')?.focus();
          setAutofocusAlreadyRun(true);
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [usernameRef, shouldFocus]);

  const { formProps } = useForm({
    fields: {},
    onSubmit: () =>
      isWorkforceSSO ? onSubmitWorkforceSSO() : onSubmitPassword(usernameProps.value, passwordProps.value),
  });

  const checkboxField = useControlledField({
    onChange: (value) => {
      setRememberMe(value);
    },
    type: 'checkbox',
    value: rememberMe,
  });

  const webauthnRegFormProps = useControlledField({
    onChange: () => setRequestWebauthnRegistration(!requestWebauthnRegistration),
    type: 'checkbox',
    value: requestWebauthnRegistration,
  });

  const isSubmitButtonEnabled =
    !isCheckingPassword && !isCheckingStatus && (isWorkforceSSO || (!passwordProps.error && !usernameProps.error));

  //autofocus on the login with passkey button
  useEffect(() => {
    if (buttonRef?.current) {
      const timeout = setTimeout(() => {
        buttonRef?.current?.focus();
      }, 300);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [buttonRef]);

  const handleBeginWebauthnLogin = async () => {
    authnClient.changeAuthMethod('legacy');
    return authnClient
      .signIn({ useWebauthn: true })
      .then((res: VerifyAuthResponse) => onHandleWebauthnSignIn(res))
      .catch((err) => onHandleWebauthnError(err));
  };

  const handleAuthTenantSSO = () => {
    const ssoTenant = localStorageHelper.get<SsoTenant>(AuthStorage.sso_tenant);
    return routerNavigate({
      to: '/sso/$tenant',
      params: {
        tenant: ssoTenant?.tenant ?? '',
      },
    });
  };

  return (
    <form id='login-form' data-testid='login-form' {...formProps}>
      <FormRow
        css={css`
          text-align: initial;
          margin: ${theme.spacing(4, 0)};
        `}
      >
        <EmailField ref={usernameRef} name='username' {...usernameProps} label={t('Email')} />
      </FormRow>

      {!isWorkforceSSO && (
        <FormRow
          css={css`
            text-align: initial;
            margin: ${theme.spacing(0, 0, 1)};
          `}
        >
          <PasswordField
            active={passwordProps.active}
            aria-invalid={passwordProps['aria-invalid']}
            error={passwordProps.error}
            id={passwordProps.id}
            label={t('Password')}
            minChars={(passwordProps as PasswordProps).minChars}
            name='password'
            onBlur={() => passwordProps.onBlur()}
            onChange={passwordProps.onChange}
            onFocus={() => passwordProps.onFocus()}
            required={passwordProps.required}
            touched={passwordProps.touched}
            value={passwordProps.value.toString()}
          />
        </FormRow>
      )}

      <TextLink
        css={css`
          display: flex;
          margin: ${theme.spacing(0, 0, 4)};
          cursor: pointer;
          text-align: left;
          width: 100%;
        `}
        onClick={() => onClickForgotPassword(usernameProps.value)}
        id='forgot-password-button'
      >
        {t('Forgot password?')}
      </TextLink>

      {!isWorkforceSSO && !isInMobileApp && (
        <FormRow>
          <CheckboxField name='Remember Me' {...checkboxField} label={t('Remember me')} id='remember-me' />
        </FormRow>
      )}

      {showWebAuthLogin && !isInMobileApp && (
        <FormRow>
          <CheckboxField
            name={'request-webauthn-registration'}
            {...webauthnRegFormProps}
            label={t('Set up new passkey')}
            value={requestWebauthnRegistration}
            id='set-up-new-passkey-button'
          />
        </FormRow>
      )}

      <Button
        variant='primary'
        disabled={!isSubmitButtonEnabled}
        size='large'
        type='submit'
        css={{ width: '100%' }}
        id='main-log-in-button'
      >
        {isCheckingPassword
          ? t('Verifying...')
          : isCheckingStatus
          ? t('Checking Status...')
          : isWorkforceSSO
          ? t('Proceed with SSO')
          : t('Log In')}
      </Button>

      {((showWebAuthLogin && !isInMobileApp) || showAuthTenantSSO) && (
        <>
          <div
            css={css`
              display: flex;
              align-items: center;
              text-align: center;
              margin: ${theme.spacing(2, 0)};

              &::before,
              &::after {
                content: '';
                flex: 1;
                height: 1px;
                background: ${theme.colors.neutral50};
              }
            `}
          ></div>

          {showWebAuthLogin &&
            !isInMobileApp && ( // we don't want to show passkey login in mobile apps until it's supported
              <Button
                variant='secondary'
                size='large'
                css={{ width: '100%' }}
                ref={buttonRef}
                onClick={handleBeginWebauthnLogin}
                id='log-in-passkey-button'
              >
                {t('Log In with Passkey')}
              </Button>
            )}

          {showAuthTenantSSO && (
            <Button
              variant='secondary'
              size='large'
              css={{ width: '100%', marginTop: '10px' }}
              onClick={handleAuthTenantSSO}
              id='log-in-sso-button'
            >
              {t('Log In with SSO')}
            </Button>
          )}
        </>
      )}
    </form>
  );
};
